import type { SelectChangeEvent } from "@mui/material";
import { Checkbox, ListItemText, MenuItem, Select, Typography } from "@mui/material";
import type { GridRenderEditCellParams, GridValidRowModel } from "@mui/x-data-grid-premium";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import { useLayoutEffect, useRef } from "react";

interface Props extends GridRenderEditCellParams<GridValidRowModel, string[]> {
  valueOptions: { value: string; label: string }[];
  commitChangesOnSelect?: boolean;
}

const GridEditMultiSelectCell = ({ valueOptions, commitChangesOnSelect, id, field, value, hasFocus }: Props) => {
  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (hasFocus) {
      inputRef.current?.focus();
    }
  }, [hasFocus]);

  const handleValueChange = (e: SelectChangeEvent<string[]>) => {
    const newValue = e.target.value;
    if (Array.isArray(newValue)) {
      apiRef.current.setEditCellValue({ id, field, value: newValue });
      if (commitChangesOnSelect) {
        apiRef.current.stopCellEditMode({ id, field });
      }
    }
  };

  const selectedValues = Array.isArray(value) ? value : [];
  const invalidValues = selectedValues.filter((val) => !valueOptions.some((o) => o.value === val));

  return (
    <Select
      multiple
      displayEmpty
      value={selectedValues}
      renderValue={(selected) => selected.join(", ")}
      onChange={handleValueChange}
      inputRef={inputRef}
      sx={{
        width: "100%",
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
      }}
    >
      {invalidValues.map((val) => (
        <MenuItem key={val} value={val} dense>
          <Checkbox checked={selectedValues.includes(val)} />
          <ListItemText sx={(t) => ({ maxWidth: "12rem", border: `1px solid ${t.palette.error.main}` })}>
            <Typography noWrap>{val}</Typography>
          </ListItemText>
        </MenuItem>
      ))}

      {valueOptions.map(({ value, label }) => (
        <MenuItem key={value} value={value} dense>
          <Checkbox checked={selectedValues.includes(value)} />
          <ListItemText sx={{ maxWidth: "12rem" }}>
            <Typography noWrap>{label}</Typography>
          </ListItemText>
        </MenuItem>
      ))}
    </Select>
  );
};

export default GridEditMultiSelectCell;
